/**
 * Crypto Settings Component
 * Admin panel to manage crypto wallet addresses and tax tracking
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Wallet, Save, Loader2, RefreshCw, Copy, Check,
  AlertTriangle, Receipt, DollarSign, Building,
  Calendar, Filter, Home
} from "lucide-react";

const NETWORKS = [
  { id: 'bitcoin', name: 'Bitcoin', icon: '₿', placeholder: 'bc1q...', taxWallet: 'tax_wallet_btc' },
  { id: 'solana', name: 'Solana', icon: '◎', placeholder: 'Base58 address...', taxWallet: 'tax_wallet_sol' },
  { id: 'tron', name: 'Tron', icon: '⚡', placeholder: 'T...', taxWallet: 'tax_wallet_tron' },
  { id: 'ethereum', name: 'Ethereum', icon: '⟠', placeholder: '0x...', taxWallet: 'tax_wallet_evm' },
  { id: 'polygon', name: 'Polygon', icon: '🟣', placeholder: '0x...', taxWallet: 'tax_wallet_evm' },
  { id: 'arbitrum', name: 'Arbitrum', icon: '🔷', placeholder: '0x...', taxWallet: 'tax_wallet_evm' },
  { id: 'base', name: 'Base', icon: '🔵', placeholder: '0x...', taxWallet: 'tax_wallet_evm' },
];

export default function CryptoSettingsView({ API }) {
  const [wallets, setWallets] = useState({
    bitcoin: '',
    solana: '',
    tron: '',
    ethereum: '',
    polygon: '',
    arbitrum: '',
    base: '',
    tax_wallet_evm: '',
    tax_wallet_btc: '',
    tax_wallet_sol: '',
    tax_wallet_tron: '',
    tax_rate: 10.31,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [activeTab, setActiveTab] = useState('wallets');
  const [taxReport, setTaxReport] = useState(null);
  const [loadingTax, setLoadingTax] = useState(false);
  // Date filtering state
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month', 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'tax') {
      fetchTaxReport();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/crypto-settings`);
      setWallets(res.data);
    } catch (err) {
      console.error('Failed to fetch crypto settings:', err);
      toast.error('Failed to load crypto settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxReport = async (customStart = null, customEnd = null) => {
    try {
      setLoadingTax(true);
      
      // Calculate date range based on filter
      let start = customStart;
      let end = customEnd;
      
      if (!customStart && !customEnd) {
        const today = new Date();
        
        if (dateFilter === 'today') {
          start = today.toISOString().split('T')[0];
          end = start;
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          start = weekAgo.toISOString().split('T')[0];
          end = today.toISOString().split('T')[0];
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          start = monthAgo.toISOString().split('T')[0];
          end = today.toISOString().split('T')[0];
        } else if (dateFilter === 'custom') {
          start = startDate || null;
          end = endDate || null;
        }
      }
      
      // Build URL with query params
      let url = `${API}/admin/crypto-tax-report`;
      const params = [];
      if (start) params.push(`start_date=${start}`);
      if (end) params.push(`end_date=${end}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const res = await axios.get(url);
      setTaxReport(res.data);
    } catch (err) {
      console.error('Failed to fetch tax report:', err);
      toast.error('Failed to load tax report');
    } finally {
      setLoadingTax(false);
    }
  };
  
  // Refetch when date filter changes
  useEffect(() => {
    if (activeTab === 'tax') {
      fetchTaxReport();
    }
  }, [dateFilter, startDate, endDate]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/admin/crypto-settings`, wallets);
      toast.success('Crypto wallet settings saved!');
    } catch (err) {
      console.error('Failed to save crypto settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setWallets(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const copyAddress = async (address, id) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(id);
      toast.success('Address copied!');
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const syncEVMAddresses = () => {
    const evmAddress = wallets.ethereum || wallets.polygon || wallets.arbitrum || wallets.base;
    if (evmAddress) {
      setWallets(prev => ({
        ...prev,
        ethereum: evmAddress,
        polygon: evmAddress,
        arbitrum: evmAddress,
        base: evmAddress,
      }));
      toast.success('EVM addresses synced!');
    } else {
      toast.error('Enter at least one EVM address first');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-vault-gold animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight flex items-center gap-3">
          <Wallet className="w-8 h-8 text-vault-gold" />
          Crypto Payment Settings
        </h1>
        <p className="text-vault-text-secondary font-manrope mt-1">
          Configure wallet addresses and track tax obligations
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'wallets' ? 'default' : 'outline'}
          onClick={() => setActiveTab('wallets')}
          className={activeTab === 'wallets' ? 'bg-vault-gold text-black' : 'border-vault-border'}
        >
          <Wallet className="w-4 h-4 mr-2" />
          Wallet Addresses
        </Button>
        <Button
          variant={activeTab === 'tax' ? 'default' : 'outline'}
          onClick={() => setActiveTab('tax')}
          className={activeTab === 'tax' ? 'bg-vault-gold text-black' : 'border-vault-border'}
        >
          <Receipt className="w-4 h-4 mr-2" />
          Tax Report
        </Button>
      </div>

      {activeTab === 'wallets' && (
        <>
          {/* Info Card */}
          <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-medium">Important</p>
                  <p className="text-amber-200/70 text-sm">
                    Room payments go to main wallets. Set up tax wallets to track how much to transfer periodically.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tax Rate */}
          <Card className="bg-vault-surface border-vault-border mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-vault-text">Tax Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  step="0.01"
                  value={wallets.tax_rate}
                  onChange={(e) => handleChange('tax_rate', parseFloat(e.target.value) || 0)}
                  className="bg-zinc-900 border-vault-border text-white w-32"
                />
                <span className="text-vault-text-secondary">%</span>
                <p className="text-vault-text-secondary text-sm">
                  Tax portion will be calculated and tracked for each payment
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Main Wallets - Non-EVM */}
          <Card className="bg-vault-surface border-vault-border mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-vault-text">Non-EVM Networks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {NETWORKS.filter(n => ['bitcoin', 'solana', 'tron'].includes(n.id)).map(network => (
                <div key={network.id} className="space-y-2">
                  <label className="flex items-center gap-2 text-vault-text font-medium">
                    <span className="text-xl">{network.icon}</span>
                    {network.name} - Main Wallet
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={wallets[network.id] || ''}
                      onChange={(e) => handleChange(network.id, e.target.value)}
                      placeholder={network.placeholder}
                      className="bg-zinc-900 border-vault-border text-white font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyAddress(wallets[network.id], network.id)}
                      className="border-vault-border"
                      disabled={!wallets[network.id]}
                    >
                      {copied === network.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  
                  <label className="flex items-center gap-2 text-vault-text-secondary text-sm ml-7">
                    <Building className="w-4 h-4" />
                    Tax Wallet
                  </label>
                  <div className="flex gap-2 ml-7">
                    <Input
                      value={wallets[network.taxWallet] || ''}
                      onChange={(e) => handleChange(network.taxWallet, e.target.value)}
                      placeholder={`Tax wallet for ${network.name}`}
                      className="bg-zinc-900 border-vault-border text-white font-mono text-sm"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Main Wallets - EVM */}
          <Card className="bg-vault-surface border-vault-border mb-4">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-vault-text">EVM Networks</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={syncEVMAddresses}
                  className="border-vault-border text-vault-text-secondary hover:text-vault-text"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync All EVM
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {NETWORKS.filter(n => ['ethereum', 'polygon', 'arbitrum', 'base'].includes(n.id)).map(network => (
                <div key={network.id} className="space-y-2">
                  <label className="flex items-center gap-2 text-vault-text font-medium">
                    <span className="text-xl">{network.icon}</span>
                    {network.name}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={wallets[network.id] || ''}
                      onChange={(e) => handleChange(network.id, e.target.value)}
                      placeholder={network.placeholder}
                      className="bg-zinc-900 border-vault-border text-white font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyAddress(wallets[network.id], network.id)}
                      className="border-vault-border"
                      disabled={!wallets[network.id]}
                    >
                      {copied === network.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ))}
              
              <div className="pt-4 border-t border-vault-border">
                <label className="flex items-center gap-2 text-vault-text-secondary text-sm">
                  <Building className="w-4 h-4" />
                  EVM Tax Wallet (used for all EVM chains)
                </label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={wallets.tax_wallet_evm || ''}
                    onChange={(e) => handleChange('tax_wallet_evm', e.target.value)}
                    placeholder="0x... (tax wallet for ETH, Polygon, Arbitrum, Base)"
                    className="bg-zinc-900 border-vault-border text-white font-mono text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-vault-gold hover:bg-vault-gold/90 text-black font-bold px-8"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Save className="w-5 h-5 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </>
      )}

      {activeTab === 'tax' && (
        <>
          {/* Date Filter */}
          <Card className="bg-vault-surface border-vault-border mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-vault-text-secondary" />
                  <span className="text-vault-text-secondary text-sm">Filter by:</span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'all', label: 'All Time' },
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'Last 7 Days' },
                    { value: 'month', label: 'Last 30 Days' },
                    { value: 'custom', label: 'Custom' },
                  ].map(option => (
                    <Button
                      key={option.value}
                      variant={dateFilter === option.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDateFilter(option.value)}
                      className={dateFilter === option.value ? 'bg-vault-gold text-black' : 'border-vault-border text-vault-text-secondary'}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
                
                {dateFilter === 'custom' && (
                  <div className="flex items-center gap-2 mt-2 w-full md:w-auto md:mt-0">
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="bg-zinc-900 border-vault-border text-white w-40"
                    />
                    <span className="text-vault-text-secondary">to</span>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="bg-zinc-900 border-vault-border text-white w-40"
                    />
                  </div>
                )}
              </div>
              
              {taxReport?.date_range?.filtered && (
                <p className="text-vault-text-secondary text-xs mt-2">
                  Showing data from {taxReport.date_range.start || 'beginning'} to {taxReport.date_range.end || 'now'}
                </p>
              )}
            </CardContent>
          </Card>
          
          {loadingTax ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-vault-gold animate-spin" />
            </div>
          ) : taxReport ? (
            <>
              {/* Wallet Totals - Auto-Split */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Card className="bg-gradient-to-br from-green-900/30 to-green-800/10 border-green-500/30">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center">
                        <Home className="w-7 h-7 text-green-400" />
                      </div>
                      <div>
                        <p className="text-green-300/70 text-sm font-medium">Main Wallet (Room Rate)</p>
                        <p className="text-green-400 text-3xl font-bold">${taxReport.main_wallet_total?.toFixed(2) || '0.00'}</p>
                        <p className="text-green-300/50 text-xs mt-1">Your revenue after tax is separated</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/10 border-blue-500/30">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center">
                        <Building className="w-7 h-7 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-blue-300/70 text-sm font-medium">Tax Wallet (Sales Tax)</p>
                        <p className="text-blue-400 text-3xl font-bold">${taxReport.tax_wallet_total?.toFixed(2) || '0.00'}</p>
                        <p className="text-blue-300/50 text-xs mt-1">Auto-calculated {taxReport.tax_rate}% sales tax</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Auto-Split Info */}
              <Card className="bg-emerald-500/10 border-emerald-500/30 mb-6">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-emerald-400 font-medium">Automatic Tax Tracking</p>
                      <p className="text-emerald-200/70 text-sm">
                        When crypto payments come in, the system automatically calculates and separates the {taxReport.tax_rate}% sales tax portion. 
                        The totals above show your Room Rate vs Sales Tax breakdown.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-vault-surface border-vault-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-vault-text-secondary text-xs">Total Revenue</p>
                        <p className="text-vault-text text-lg font-bold">${taxReport.total_revenue}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-vault-surface border-vault-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <Receipt className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-vault-text-secondary text-xs">Total Tax ({taxReport.tax_rate}%)</p>
                        <p className="text-purple-400 text-lg font-bold">${taxReport.total_tax_amount?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-vault-surface border-vault-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-vault-text-secondary text-xs">Tax Pending</p>
                        <p className="text-amber-400 text-lg font-bold">${taxReport.total_tax_pending.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-vault-surface border-vault-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-vault-text-secondary text-xs">Payments</p>
                        <p className="text-vault-text text-lg font-bold">{taxReport.total_payments}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* By Network */}
              <Card className="bg-vault-surface border-vault-border">
                <CardHeader>
                  <CardTitle className="text-lg text-vault-text">Tax by Network</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {NETWORKS.map(network => {
                      const data = taxReport.by_network[network.id];
                      if (!data || data.total_payments === 0) return null;
                      
                      // Calculate room rate portion (total - tax)
                      const roomRate = data.total_amount - data.tax_amount;
                      
                      return (
                        <div key={network.id} className="p-4 bg-zinc-900 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{network.icon}</span>
                              <span className="text-vault-text font-medium">{network.name}</span>
                              <span className="text-vault-text-secondary text-sm">
                                ({data.total_payments} payment{data.total_payments !== 1 ? 's' : ''})
                              </span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-vault-text-secondary">Total Received</p>
                              <p className="text-vault-text font-medium">${data.total_amount.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-green-400/70">Room Rate</p>
                              <p className="text-green-400 font-medium">${roomRate.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-blue-400/70">Sales Tax</p>
                              <p className="text-blue-400 font-medium">${data.tax_amount.toFixed(2)}</p>
                            </div>
                          </div>
                          
                          {data.tax_wallet && (
                            <div className="mt-3 pt-3 border-t border-vault-border">
                              <p className="text-vault-text-secondary text-xs">Tax Wallet:</p>
                              <code className="text-vault-text text-xs font-mono">{data.tax_wallet}</code>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {Object.values(taxReport.by_network).every(d => d.total_payments === 0) && (
                      <p className="text-vault-text-secondary text-center py-8">
                        No crypto payments recorded yet
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Button
                variant="outline"
                onClick={fetchTaxReport}
                className="mt-4 border-vault-border"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Report
              </Button>
            </>
          ) : (
            <p className="text-vault-text-secondary text-center py-8">
              Failed to load tax report
            </p>
          )}
        </>
      )}
    </>
  );
}
